import { useState, useContext } from 'react';
import { Panel } from '@appkit4/react-components/panel';
import { CalendarPicker } from '@appkit4/react-components/datepicker';
import { Select } from '@appkit4/react-components/select';
import { Combobox } from '@appkit4/react-components/combobox';
import { Button } from '@appkit4/react-components/button';
import { UserContext } from '@contexts/UserProvider';
import { isUserAdmin } from '@utils/users';
import TokenDataContainer from './TokenDataContainer';
import { titleMap } from './constants';
import type { FilterState } from './types';
import dayjs from 'dayjs';
import styles from './Analytics.module.scss';

const Analytics = () => {
    const { user } = useContext(UserContext);

    const [startTime, setStartTime] = useState(dayjs().startOf('month').startOf('day'));
    const [endTime, setEndTime] = useState(dayjs().endOf('day'));
    const [application, setApplication] = useState('All');
    const [userList, setUserList] = useState<string[]>([]);
    const [selectedUserList, setSelectedUserList] = useState<string[]>([]);
    const [filter, setFilter] = useState<FilterState>({
        startTime: dayjs().startOf('month'),
        endTime: dayjs(),
        application: 'All',
        selectedUserList: [],
    });

    const handleDateChange = (date: Date | Date[], isValid: boolean) => {
        if (!isValid) return;
        if (Array.isArray(date) && date.length === 2) {
            setStartTime(dayjs(date[0]).startOf('day'));
            setEndTime(dayjs(date[1]).endOf('day'));
        }
    };

    const handleSelect = (value: string | number | (string | number)[]) => {
        if (typeof value === 'string') {
            setApplication(value);
        }
    };

    const handleSelectUser = (value: string | number | (string | number)[]) => {
        if (Array.isArray(value)) {
            setSelectedUserList(value.map((v) => String(v)));
        }
    };

    const applyFilter = () => {
        const nextFilter: FilterState = {
            selectedUserList,
            startTime,
            endTime,
            application,
        };
        setFilter(nextFilter);
    };

    const selectOptionsData = Object.entries(titleMap).map((item) => ({
        value: item[0],
        label: item[1],
    }));

    // Check if user is admin
    if (!isUserAdmin(user)) {
        return (
            <Panel className={styles['analytics-page']}>
                <div className={styles['header-section']}>
                    <h1 className={styles['page-title']}>Access Denied</h1>
                    <p>Only administrators can access AI Usage Analytics.</p>
                </div>
            </Panel>
        );
    }

    return (
        <Panel className={styles['analytics-page']}>
            <div className={styles['header-section']}>
                <h1 className={styles['page-title']}>AI Usage Analytics</h1>
            </div>

            <div className={styles['filter-section']}>
                <CalendarPicker
                    defaultValue={[startTime.toDate(), endTime.toDate()]}
                    mode="showRangePanel"
                    selectRange
                    placeholder="start_time end_time"
                    fieldTitle="Token Usage Date Range"
                    onChange={handleDateChange}
                    fieldWidth={237}
                />
                <Combobox
                    multiple
                    style={{ width: 237 }}
                    placeholder="Team Member"
                    data={userList.map((email) => ({
                        value: email,
                        label: email,
                    }))}
                    onSelect={handleSelectUser}
                />
                <Select style={{ width: 237 }} placeholder="AI Tool" data={selectOptionsData} onSelect={handleSelect} />
                <Button kind="primary" onClick={applyFilter}>
                    Apply Filter
                </Button>
            </div>

            <TokenDataContainer env="all" parentSetUserList={setUserList} filter={filter} />
        </Panel>
    );
};

export default Analytics;
